<template>
  <div class="markdown-renderer" ref="markdownContainer">
    <div class="markdown-body" v-html="renderedContent"></div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import mermaid from 'mermaid/dist/mermaid.min.js';

export default {
  name: 'MarkdownRenderer',
  
  props: {
    content: {
      type: String,
      default: ''
    },
    enableKatex: {
      type: Boolean,
      default: false
    },
    enableMermaid: {
      type: Boolean,
      default: true
    },
    enableHighlight: {
      type: Boolean,
      default: true
    },
    streaming: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      md: null,
      renderedContent: '',
      renderTimer: null,
      renderBuffer: '',
      mermaidInitialized: false
    };
  },
  
  computed: {
    contentToRender() {
      return this.streaming ? this.renderBuffer : this.content;
    }
  },
  
  watch: {
    content: {
      handler(newContent) {
        if (this.md) {
          this.renderContent(newContent);
        }
      },
      immediate: true
    }
  },
  
  created() {
    this.initMarkdownIt();
    if (this.enableMermaid) {
      this.initMermaid();
    }
  },
  
  mounted() {
    // 在挂载后渲染初始内容
    // 🔥 P0修复：即使 streaming=true，如果有 content 也要渲染
    // （切换会话恢复时，消息可能还标记为 streaming，但实际已有完整内容）
    if (this.content) {
      this.renderContent(this.content);
    }
  },
  
  methods: {
    // 初始化 MarkdownIt
    initMarkdownIt() {
      this.md = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
        breaks: false,  // 禁用单换行转br，减少间距
        highlight: (str, lang) => {
          if (!this.enableHighlight) {
            return '';
          }
          
          if (lang && hljs.getLanguage(lang)) {
            try {
              const highlighted = hljs.highlight(str, { language: lang, ignoreIllegals: true }).value;
              return `<pre class="hljs"><code class="language-${lang}">${highlighted}</code></pre>`;
            } catch (err) {
              console.error('Highlight error:', err);
            }
          }
          
          return `<pre class="hljs"><code>${this.md.utils.escapeHtml(str)}</code></pre>`;
        }
      });
      
      // 配置插件
      this.setupPlugins();
    },
    
    // 设置插件
    setupPlugins() {
      // 添加表格支持
      this.md.renderer.rules.table_open = () => '<table class="markdown-table">\n';

      // 自定义列表项渲染 - 让文本内容和序号在同一行
      const defaultListItemOpen = this.md.renderer.rules.list_item_open || function(tokens, idx, options, env, self) {
        return self.renderToken(tokens, idx, options);
      };

      this.md.renderer.rules.list_item_open = (tokens, idx, options, env, self) => {
        return defaultListItemOpen(tokens, idx, options, env, self);
      };

      // 移除列表项内段落的换行
      this.md.renderer.rules.paragraph_open = (tokens, idx, options, env, self) => {
        // 检查是否在列表项内
        let inListItem = false;
        for (let i = idx - 1; i >= 0; i--) {
          if (tokens[i].type === 'list_item_open') {
            inListItem = true;
            break;
          }
          if (tokens[i].type === 'list_item_close') {
            break;
          }
        }
        // 如果在列表项内，不输出任何标签
        return inListItem ? '' : '<p>';
      };

      this.md.renderer.rules.paragraph_close = (tokens, idx, options, env, self) => {
        // 检查是否在列表项内
        let inListItem = false;
        for (let i = idx - 1; i >= 0; i--) {
          if (tokens[i].type === 'list_item_open') {
            inListItem = true;
            break;
          }
          if (tokens[i].type === 'list_item_close') {
            break;
          }
        }
        // 如果在列表项内，不输出任何标签
        return inListItem ? '' : '</p>';
      };

      // 添加任务列表支持
      this.md.use(this.taskListPlugin);

      // 添加数学公式支持
      if (this.enableKatex) {
        this.md.use(this.katexPlugin);
      }

      // 自定义链接渲染
      const defaultLinkRender = this.md.renderer.rules.link_open || function(tokens, idx, options, env, self) {
        return self.renderToken(tokens, idx, options);
      };
      
      this.md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
        const aIndex = tokens[idx].attrIndex('target');
        if (aIndex < 0) {
          tokens[idx].attrPush(['target', '_blank']);
        } else {
          tokens[idx].attrs[aIndex][1] = '_blank';
        }
        tokens[idx].attrPush(['rel', 'noopener noreferrer']);
        return defaultLinkRender(tokens, idx, options, env, self);
      };
      
      // 代码块增强
      const defaultFenceRender = this.md.renderer.rules.fence;
      this.md.renderer.rules.fence = (tokens, idx, options, env, self) => {
        const token = tokens[idx];
        const lang = token.info.trim();
        
        // Mermaid 图表处理
        if (lang === 'mermaid' && this.enableMermaid) {
          const mermaidId = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return `<div class="mermaid-container"><div id="${mermaidId}" class="mermaid">${this.md.utils.escapeHtml(token.content)}</div></div>`;
        }
        
        // 普通代码块
        const highlighted = defaultFenceRender(tokens, idx, options, env, self);
        const langClass = lang ? `language-${lang}` : '';
        
        return `
          <div class="code-block-wrapper">
            <div class="code-block-header">
              <span class="code-language">${lang || 'plaintext'}</span>
              <button class="copy-button" onclick="this.dataset.copied='true'; navigator.clipboard.writeText(this.parentElement.nextElementSibling.querySelector('code').textContent); setTimeout(() => delete this.dataset.copied, 2000)">
                <span class="copy-text">复制</span>
                <span class="copied-text">已复制</span>
              </button>
            </div>
            <div class="code-block-content ${langClass}">
              ${highlighted}
            </div>
          </div>
        `;
      };
    },
    
    // 任务列表插件
    taskListPlugin(md) {
      md.core.ruler.after('inline', 'task_list', state => {
        const tokens = state.tokens;
        for (let i = 0; i < tokens.length; i++) {
          if (tokens[i].type !== 'inline') continue;
          
          const content = tokens[i].content;
          const checkboxRegex = /^\[([ x])\]\s/i;
          
          if (checkboxRegex.test(content)) {
            const checked = content[1].toLowerCase() === 'x';
            const checkbox = checked 
              ? '<input type="checkbox" class="task-list-item-checkbox" checked disabled>'
              : '<input type="checkbox" class="task-list-item-checkbox" disabled>';
            
            tokens[i].content = content.replace(checkboxRegex, checkbox + ' ');
            
            // 添加任务列表类
            if (tokens[i - 2] && tokens[i - 2].type === 'list_item_open') {
              tokens[i - 2].attrPush(['class', 'task-list-item']);
            }
          }
        }
      });
    },
    
    // KaTeX 插件
    katexPlugin(md) {
      // 行内公式 $ ... $
      md.inline.ruler.after('escape', 'math_inline', (state, silent) => {
        const src = state.src;
        const pos = state.pos;
        
        if (src[pos] !== '$') return false;
        
        const end = src.indexOf('$', pos + 1);
        if (end === -1) return false;
        
        if (!silent) {
          const token = state.push('math_inline', 'math', 0);
          token.content = src.slice(pos + 1, end);
        }
        
        state.pos = end + 1;
        return true;
      });
      
      // 块级公式 $$ ... $$
      md.block.ruler.after('fence', 'math_block', (state, startLine, endLine, silent) => {
        const pos = state.bMarks[startLine] + state.tShift[startLine];
        const max = state.eMarks[startLine];
        const src = state.src;
        
        if (pos + 2 > max) return false;
        if (src.slice(pos, pos + 2) !== '$$') return false;
        
        let nextLine = startLine + 1;
        while (nextLine < endLine) {
          const nextPos = state.bMarks[nextLine] + state.tShift[nextLine];
          const nextMax = state.eMarks[nextLine];
          
          if (src.slice(nextPos, nextPos + 2) === '$$') {
            if (!silent) {
              const token = state.push('math_block', 'math', 0);
              token.content = state.getLines(startLine + 1, nextLine, state.tShift[startLine], true);
            }
            state.line = nextLine + 1;
            return true;
          }
          nextLine++;
        }
        
        return false;
      });
      
      // 渲染器
      md.renderer.rules.math_inline = (tokens, idx) => {
        try {
          return katex.renderToString(tokens[idx].content, { throwOnError: false });
        } catch (e) {
          return `<span class="math-error">${tokens[idx].content}</span>`;
        }
      };
      
      md.renderer.rules.math_block = (tokens, idx) => {
        try {
          return `<div class="math-block">${katex.renderToString(tokens[idx].content, { 
            displayMode: true,
            throwOnError: false 
          })}</div>`;
        } catch (e) {
          return `<div class="math-error">${tokens[idx].content}</div>`;
        }
      };
    },
    
    // 初始化 Mermaid
    initMermaid() {
      if (this.mermaidInitialized) return;
      
      try {
        // 使用与成功的HTML页面相同的配置
        mermaid.initialize({
          startOnLoad: false,  // 手动控制渲染
          theme: 'default',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: 'basis'
          },
          securityLevel: 'loose'  // 允许所有内容
        });
        
        // 设置错误处理
        mermaid.parseError = function(err, hash) {
          console.warn('Mermaid parse error:', err);
        };
        
        this.mermaidInitialized = true;
      } catch (error) {
        console.error('Mermaid init error:', error);
      }
    },
    
    // 渲染内容
    renderContent(content) {
      if (!content) {
        this.renderedContent = '';
        return;
      }
      
      // 确保 MarkdownIt 已初始化
      if (!this.md) {
        console.warn('MarkdownIt not initialized, initializing now...');
        this.initMarkdownIt();
      }
      
      try {
        this.renderedContent = this.md.render(content);
        
        // 渲染 Mermaid 图表
        if (this.enableMermaid) {
          this.$nextTick(() => {
            this.renderMermaidCharts();
          });
        }
        
        this.$emit('rendered');
      } catch (error) {
        console.error('Markdown render error:', error);
        this.renderedContent = `<pre>${this.escapeHtml(content)}</pre>`;
      }
    },
    
    // 渲染 Mermaid 图表
    async renderMermaidCharts() {
      if (!this.enableMermaid || !this.$refs.markdownContainer) return;

      const mermaidElements = this.$refs.markdownContainer.querySelectorAll('.mermaid');
      if (mermaidElements.length === 0) return;

      // 确保 Mermaid 已初始化
      if (!this.mermaidInitialized) {
        this.initMermaid();
      }

      // 清除旧的处理标记，确保每次都重新渲染
      mermaidElements.forEach((el) => {
        delete el.dataset.processed;
        el.classList.remove('mermaid-rendered', 'mermaid-fallback-mode');
      });

      try {
        mermaid.init(undefined, mermaidElements);

        mermaidElements.forEach((el) => {
          el.dataset.processed = 'true';
          el.classList.add('mermaid-rendered');
        });
      } catch (error) {
        console.warn('Mermaid batch render failed, falling back to per-element render:', error);

        mermaidElements.forEach((element, index) => {
          try {
            const graphDefinition = element.textContent.trim();
            if (!graphDefinition) {
              return;
            }

            const graphId = `mermaid-${Date.now()}-${index}`;
            element.id = graphId;

            mermaid.init(undefined, element);

            element.dataset.processed = 'true';
            element.classList.add('mermaid-rendered');
          } catch (renderError) {
            element.innerHTML = `
              <div class="mermaid-fallback">
                <div class="mermaid-notice">
                  <span class="notice-icon">📊</span>
                  <span class="notice-text">Mermaid 图表</span>
                </div>
                <pre class="mermaid-source">${this.escapeHtml(element.textContent)}</pre>
              </div>
            `;
            element.classList.add('mermaid-fallback-mode');
          }
        });
      }
    },
    
    // 更新内容（流式渲染）
    updateContent(content) {
      this.renderBuffer = content;
      
      // 使用防抖优化渲染
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => {
        this.renderContent(this.renderBuffer);
      }, 50);
    },
    
    // 完成流式渲染
    finishStreaming() {
      clearTimeout(this.renderTimer);
      this.renderContent(this.renderBuffer);
    },
    
    // 获取渲染后的HTML
    getRenderedHTML() {
      return this.renderedContent;
    },
    
    // HTML转义
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    }
  },
  
  beforeDestroy() {
    clearTimeout(this.renderTimer);
  }
};
</script>

<style lang="scss">
/* 不使用 scoped，让样式直接应用 */
.markdown-renderer {
  width: 100%;
  overflow: visible;
  font-family: var(--vscode-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif);
  font-size: 14px !important;
  color: var(--vscode-editor-foreground, #333);
  word-wrap: break-word;
}

.markdown-renderer * {
  line-height: 1.25;
}

.markdown-renderer p {
  white-space: pre-wrap;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.25 !important;
}

.markdown-renderer ol,
.markdown-renderer ul {
  padding-left: 3em !important;
  margin: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  line-height: 1.25 !important;
  display: block !important;
  list-style-position: outside !important;
}

.markdown-renderer ol {
  list-style-type: decimal !important;
}

.markdown-renderer ul {
  list-style-type: disc !important;
}

.markdown-renderer ul ul {
  list-style-type: circle !important;
  margin-top: 0 !important;
}

.markdown-renderer ul ul ul {
  list-style-type: square !important;
  margin-top: 0 !important;
}

.markdown-renderer ol ol {
  list-style-type: lower-alpha !important;
  margin-top: 0 !important;
}

.markdown-renderer ol ol ol {
  list-style-type: lower-roman !important;
  margin-top: 0 !important;
}

.markdown-renderer li {
  margin: 0 !important;
  margin-bottom: 0 !important;
  padding: 0 !important;
  line-height: 1.25 !important;
  display: list-item !important;
  list-style: inherit !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
}

.markdown-renderer li > p {
  margin: 0 !important;
  padding: 0 !important;
}

/* 列表项的直接子元素 strong、em 等设为 inline，不换行 */
.markdown-renderer li > strong,
.markdown-renderer li > em,
.markdown-renderer li > code,
.markdown-renderer li > a {
  display: inline !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* 列表项内的嵌套列表 - 紧凑显示，无额外间距 */
.markdown-renderer li > ul,
.markdown-renderer li > ol {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  display: block !important;
}


.markdown-renderer h1,
.markdown-renderer h2,
.markdown-renderer h3,
.markdown-renderer h4,
.markdown-renderer h5,
.markdown-renderer h6 {
  margin: 0 !important;
  padding: 0 !important;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-renderer h1 {
  font-size: 1.3em;
  border-bottom: 1px solid var(--vscode-panel-border, #e1e4e8);
  padding-bottom: 0.2em;
}

.markdown-renderer h2 {
  font-size: 1.2em;
  border-bottom: 1px solid var(--vscode-panel-border, #e1e4e8);
  padding-bottom: 0.15em;
}

.markdown-renderer h3 { font-size: 1.1em; }
.markdown-renderer h4 { font-size: 1em; }
.markdown-renderer h5 { font-size: 0.9em; }
.markdown-renderer h6 {
  font-size: 0.85em;
  color: var(--vscode-descriptionForeground, #6a737d);
}

.markdown-renderer > :first-child {
  margin-top: 0 !important;
}

.markdown-renderer > :last-child {
  margin-bottom: 0 !important;
}

.markdown-renderer strong {
  font-weight: 600;
  display: inline !important;
}

.markdown-renderer em {
  font-style: italic;
  display: inline !important;
}

.markdown-renderer a {
  color: var(--vscode-textLink-foreground, #0366d6);
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-decoration-color: var(--vscode-textLink-foreground, #0366d6);
}

.markdown-renderer a:hover {
  color: var(--vscode-textLink-activeForeground, #0366d6);
  text-decoration-style: solid;
  text-decoration-color: var(--vscode-textLink-activeForeground, #0366d6);
}

.markdown-renderer li.task-list-item {
  list-style: none;
  margin-left: -1.5em;
}

.markdown-renderer li.task-list-item .task-list-item-checkbox {
  margin-right: 0.5em;
}

.markdown-renderer blockquote {
  padding: 0 0.5em !important;
  color: var(--vscode-descriptionForeground, #6a737d);
  border-left: 0.25em solid var(--vscode-panel-border, #dfe2e5);
  margin: 0 !important;
}

.markdown-renderer blockquote > :first-child {
  margin-top: 0 !important;
}

.markdown-renderer blockquote > :last-child {
  margin-bottom: 0 !important;
}

.markdown-renderer table {
  border-collapse: collapse;
  margin: 0 !important;
  width: auto;
  min-width: 50%;
  max-width: 100%;
  table-layout: fixed;
  border: 1px solid #cecece;
}

.markdown-renderer th,
.markdown-renderer td {
  border: 1px solid #cecece;
  padding: 4px 8px;
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.markdown-renderer th {
  background-color: var(--vscode-editor-background, #f6f8fa);
  font-weight: 600;
  color: var(--vscode-foreground);
}

.markdown-renderer tr:nth-child(even) {
  background-color: var(--vscode-editor-inactiveSelectionBackground, rgba(0, 0, 0, 0.02));
}

.markdown-renderer tr:hover {
  background-color: var(--vscode-list-hoverBackground, rgba(0, 0, 0, 0.04));
}

.markdown-renderer code:not(pre code) {
  font-family: var(--vscode-editor-font-family, monospace);
  filter: saturate(110%) brightness(95%);
  color: var(--vscode-textPreformat-foreground) !important;
  background-color: var(--vscode-textPreformat-background) !important;
  padding: 0px 2px;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: anywhere;
  border-radius: 3px;
  font-size: 85%;
}

.markdown-renderer pre {
  margin: 0 !important;
  padding: 8px 12px !important;
  overflow-x: auto;
  background: #f6f8fa;
  border-radius: 4px;
}

.markdown-renderer pre code {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.3;
  background: transparent;
  padding: 0;
}

.markdown-renderer hr {
  height: 1px;
  padding: 0 !important;
  margin: 0 !important;
  background-color: #e1e4e8;
  border: 0;
}

.markdown-renderer img {
  max-width: 100%;
  box-sizing: border-box;
  margin: 0 !important;
  padding: 0 !important;
  display: block;
}

.markdown-renderer .katex {
  font-size: 1.1em;
  color: var(--vscode-editor-foreground, #333);
  font-family: KaTeX_Main, 'Times New Roman', serif;
  line-height: 1.2;
  white-space: normal;
  text-indent: 0;
}

.markdown-renderer .math-error {
  color: #d73a49;
  background: #ffeef0;
  padding: 2px 4px;
  border-radius: 3px;
}


</style>
